import { aiApi } from '@/api/ai'
import type { AITask, AITaskType } from '@/api/types'

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export class AITaskPollTimeoutError extends Error {
  constructor() {
    super('AI task poll timeout')
    this.name = 'AITaskPollTimeoutError'
  }
}

/** 与 backend taskRunTimeout 对齐：video 15m / image 10m / audio 5m+（长旁白按字数延长）/ music 8m */
export function pollDefaultsForTaskType(
  taskType?: AITaskType,
  modelId?: string,
  promptLength?: number,
): { intervalMs: number; maxAttempts: number } {
  if (taskType === 'audio' && modelId) {
    const id = modelId.toLowerCase()
    if (id.includes('suno') || id.includes('music-v1') || id.startsWith('music-')) {
      return { intervalMs: 3000, maxAttempts: 160 }
    }
    const baseAttempts = 150
    const extraAttempts = promptLength && promptLength > 600 ? Math.ceil((promptLength - 600) / 400) * 30 : 0
    return { intervalMs: 2000, maxAttempts: Math.min(600, baseAttempts + extraAttempts) }
  }
  switch (taskType) {
    case 'video':
      return { intervalMs: 3000, maxAttempts: 320 }
    case 'image':
      return { intervalMs: 2000, maxAttempts: 300 }
    case 'audio':
      return { intervalMs: 2000, maxAttempts: 150 }
    default:
      return { intervalMs: 2000, maxAttempts: 90 }
  }
}

export async function pollAITask(
  taskId: string,
  onUpdate: (task: AITask) => void,
  opts?: { intervalMs?: number; maxAttempts?: number; taskType?: AITaskType; modelId?: string; promptLength?: number },
): Promise<AITask> {
  const defaults = pollDefaultsForTaskType(opts?.taskType, opts?.modelId, opts?.promptLength)
  const intervalMs = opts?.intervalMs ?? defaults.intervalMs
  const maxAttempts = opts?.maxAttempts ?? defaults.maxAttempts

  for (let i = 0; i < maxAttempts; i++) {
    const task = await aiApi.getTask(taskId)
    onUpdate(task)
    if (task.status === 'succeeded' || task.status === 'failed' || task.status === 'cancelled') {
      return task
    }
    await sleep(intervalMs)
  }
  throw new AITaskPollTimeoutError()
}
